import { ReactNode, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';

import {
  DragId,
  DragItem,
  DropItem,
  INTERACTIVE_DRAG_DROP,
  InteractiveDragItem,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import { InteractiveResponseAnswerIcons } from '../../shared/interactive-response-shared';
import * as Styled from './interactive-sequence-drop.style';

export const InteractiveSequenceDrop = ({
  id,
  label,
  children,
  isCorrect,
  labelLength,
  labelBorder,
  animate,
  onDrop,
  isContinuousMarking,
  onIncorrectAnswer,
  dropItems,
  dragItems,
  isDropZoneSelected,
  onDropZoneClicked,
  isDragItemSelected,
}: {
  id: DragId;
  label: string;
  children?: ReactNode;
  isCorrect?: boolean;
  labelLength?: number | null;
  labelBorder?: boolean | null;
  animate?: boolean;
  dropItems: DropItem[];
  dragItems: DragItem[];
  onDrop: (destinationId: DragId, sourceId: DragId) => void;
  isContinuousMarking?: boolean;
  onIncorrectAnswer?: (el: HTMLDivElement) => void;
  isDropZoneSelected?: boolean;
  isDragItemSelected?: boolean;
  onDropZoneClicked?: (id: string) => void;
}) => {
  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: INTERACTIVE_DRAG_DROP,
      drop: (item: InteractiveDragItem) => {
        onDrop(id, item.id);
        return { id };
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dragItems, dropItems]
  );

  const droppedDragItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isCorrect !== undefined && isContinuousMarking) {
      setTimeout(() => {
        if (!isCorrect && droppedDragItemRef.current) {
          onIncorrectAnswer?.(droppedDragItemRef.current);
        }
      }, 1000);
    }
    //eslint-disable-next-line
  }, [isCorrect]);

  return (
    <Styled.InteractiveSequenceDropItem
      $labelLength={labelLength}
      $labelBorder={labelBorder}
      data-selector="interactive-sequence-answer"
    >
      {onDropZoneClicked ? (
        <Styled.InteractiveSequenceDropItemClickable
          $isOver={canDrop && isOver}
          $canDrop={canDrop}
          $hasDropped={!!children}
          $isCorrect={isCorrect}
          data-selector="sequence-answer-image"
          onClick={() => onDropZoneClicked(id)}
          $isOutlined={isDropZoneSelected}
          $isBackgroundHighlighted={!!isDragItemSelected}
          data-drop-zone-clickable="true"
          role="button"
        >
          <InteractiveResponseAnswerIcons
            isCorrect={isCorrect}
            animate={animate}
          />
          {children ? (
            <div ref={droppedDragItemRef}>{children}</div>
          ) : (
            <Styled.InteractiveSequenceDropItemZone
              ref={node => {
                drop(node);
              }}
            />
          )}
        </Styled.InteractiveSequenceDropItemClickable>
      ) : (
        <Styled.InteractiveSequenceDropItemTarget
          $isOver={canDrop && isOver}
          $canDrop={canDrop}
          $hasDropped={!!children}
          $isCorrect={isCorrect}
          data-selector="sequence-answer-image"
        >
          <InteractiveResponseAnswerIcons
            isCorrect={isCorrect}
            animate={animate}
          />
          {children ? (
            <div ref={droppedDragItemRef}>{children}</div>
          ) : (
            <Styled.InteractiveSequenceDropItemZone
              ref={node => {
                drop(node);
              }}
            />
          )}
        </Styled.InteractiveSequenceDropItemTarget>
      )}
      <Styled.InteractiveSequenceDropLabel data-selector="sequence-answer-label">
        {label}
      </Styled.InteractiveSequenceDropLabel>
    </Styled.InteractiveSequenceDropItem>
  );
};
